import { locationStore, useLocation } from '@/lib/location/location';

// Browser markers are separate so web can favor URLs, panels, and desktop density.
import { getConfiguredGoogleMapID } from './googleMapConfig.web';
import { getGoogleMapColorScheme, getGoogleMapStyles } from './googleMapTheme.web';
import { BoostTierBlaze, BoostTierIgnite } from '@/lib/api/apiTypes';
import { useAppearance } from '@/lib/design/appearance';
import { type DiscoveryEvent as RadiousEvent } from '@/lib/api/apiTypes';
import { formatEventTimeRange, getEventPalette } from '@/features/events/eventFormatting';
import {
  MarkerClusterer,
  SuperClusterAlgorithm,
  type Cluster,
  type ClusterStats,
  type Renderer,
} from '@googlemaps/markerclusterer';
import { AdvancedMarker, Map, useMap } from '@vis.gl/react-google-maps';
import * as React from 'react';

const ACCENT = '#ef4e2e';
declare const google: any;
type GoogleAdvancedMarker = any;
type EventPosterImage = NonNullable<RadiousEvent['poster_images']>[number];
export type WebMapRegion = {
  latitude: number;
  longitude: number;
  latitudeDelta: number;
  longitudeDelta: number;
};

type MapCameraEvent = {
  map: any;
};

function isValidMapCoordinate(coordinate: RadiousEvent['coordinate']) {
  return (
    Number.isFinite(coordinate?.latitude) &&
    Number.isFinite(coordinate?.longitude) &&
    Math.abs(coordinate.latitude) <= 90 &&
    Math.abs(coordinate.longitude) <= 180
  );
}

function registerMarkerClick(marker: GoogleAdvancedMarker, onClick: (event: any) => void) {
  const listener = marker.addListener('click', onClick);
  return () => listener.remove();
}

function registerRecenter(register: (fn: () => void) => void, recenter: () => void) {
  register(recenter);
  return () => register(() => undefined);
}

function escapeHTML(value: string) {
  return value.replace(/[&<>"']/g, (char) => {
    switch (char) {
      case '&':
        return '&amp;';
      case '<':
        return '&lt;';
      case '>':
        return '&gt;';
      case '"':
        return '&quot;';
      default:
        return '&#39;';
    }
  });
}

// Custom clusterer renderer — red pill matching the native style
const clusterRenderer: Renderer = {
  render(cluster: Cluster, _stats: ClusterStats) {
    const { count, position } = cluster;
    const marker = new google.maps.marker.AdvancedMarkerElement({
      position,
      zIndex: 50,
    });

    const div = document.createElement('div');
    div.style.cssText = `
      background: ${ACCENT};
      color: #fff;
      font-size: 13px;
      font-weight: 700;
      border-radius: 20px;
      padding: 6px 12px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.45);
      white-space: nowrap;
      cursor: pointer;
    `;
    div.textContent = String(count);
    marker.content = div;
    return marker;
  },
};

export function EventMap({
  events,
  selectedId,
  initialRegion,
  showLiveGlow: _showLiveGlow = false,
  onSelect,
  onMapMove,
  onRegionChangeComplete,
  onRecenter,
}: {
  events: RadiousEvent[];
  selectedId?: string | null;
  initialRegion?: WebMapRegion;
  showLiveGlow?: boolean;
  onSelect: (id: string) => void;
  onMapMove?: () => void;
  onRegionChangeComplete?: (region: WebMapRegion) => void;
  onRecenter?: (fn: () => void) => void;
}) {
  return (
    <MapContent
      events={events}
      selectedId={selectedId}
      initialRegion={initialRegion}
      onSelect={onSelect}
      onMapMove={onMapMove}
      onRegionChangeComplete={onRegionChangeComplete}
      onRecenter={onRecenter}
    />
  );
}

function MapContent({
  events,
  selectedId,
  initialRegion,
  onSelect,
  onMapMove,
  onRegionChangeComplete,
  onRecenter,
}: {
  events: RadiousEvent[];
  selectedId?: string | null;
  initialRegion?: WebMapRegion;
  onSelect: (id: string) => void;
  onMapMove?: () => void;
  onRegionChangeComplete?: (region: WebMapRegion) => void;
  onRecenter?: (fn: () => void) => void;
}) {
  const map = useMap();
  const resolvedMode = useAppearance((state) => state.resolvedMode);
  const mapId = getConfiguredGoogleMapID();
  const { coords } = useLocation();
  const userCenter = React.useMemo(
    () => ({ lat: coords.latitude, lng: coords.longitude }),
    [coords.latitude, coords.longitude]
  );
  const [clusterer, setClusterer] = React.useState<MarkerClusterer | null>(null);
  const markersRef = React.useRef<globalThis.Map<string, GoogleAdvancedMarker>>(
    new globalThis.Map()
  );
  const emittedInitialRegionRef = React.useRef(false);

  const defaultCenter = React.useMemo(() => {
    if (initialRegion) {
      return { lat: initialRegion.latitude, lng: initialRegion.longitude };
    }
    const { coords: c } = locationStore.getState();
    return { lat: c.latitude, lng: c.longitude };
  }, [initialRegion]);

  const handleCameraChanged = React.useCallback(
    (event: MapCameraEvent) => {
      if (!emittedInitialRegionRef.current) {
        emittedInitialRegionRef.current = true;
        return;
      }
      const center = event.map.getCenter()?.toJSON();
      const bounds = event.map.getBounds()?.toJSON();
      if (!center || !bounds) {
        return;
      }
      onRegionChangeComplete?.({
        latitude: center.lat,
        longitude: center.lng,
        latitudeDelta: Math.max(0.01, Math.abs(bounds.north - bounds.south)),
        longitudeDelta: Math.max(0.01, Math.abs(bounds.east - bounds.west)),
      });
    },
    [onRegionChangeComplete]
  );

  // Boot the clusterer once the map is ready
  React.useEffect(() => {
    if (!map) return;
    const c = new MarkerClusterer({
      map,
      renderer: clusterRenderer,
      algorithm: new SuperClusterAlgorithm({ radius: 120, maxZoom: 14 }),
      onClusterClick: (event, cluster, clusterMap) => {
        event.domEvent?.stopPropagation?.();
        if (cluster.bounds) {
          clusterMap.fitBounds(cluster.bounds);
        }
      },
    });
    setClusterer(c);
    return () => {
      c.clearMarkers();
      setClusterer(null);
    };
  }, [map]);

  // Expose recenter to parent
  React.useEffect(() => {
    if (!map || !onRecenter) return;
    return registerRecenter(onRecenter, () => {
      map.panTo(userCenter);
      map.setZoom(14);
    });
  }, [map, onRecenter, userCenter]);

  // Pan to selected event
  React.useEffect(() => {
    if (!map || !selectedId) return;
    const event = events.find((e) => e.id === selectedId);
    if (!event) return;
    map.panTo({ lat: event.coordinate.latitude, lng: event.coordinate.longitude });
    map.setZoom(15);
  }, [map, selectedId, events]);

  return (
    <Map
      defaultCenter={defaultCenter}
      defaultZoom={13}
      backgroundColor={resolvedMode === 'night' ? '#080808' : '#fff8e7'}
      colorScheme={getGoogleMapColorScheme(resolvedMode)}
      mapId={mapId}
      renderingType="VECTOR"
      gestureHandling="greedy"
      clickableIcons={false}
      disableDefaultUI
      styles={mapId ? undefined : getGoogleMapStyles(resolvedMode)}
      style={{ width: '100%', height: '100%' }}
      onIdle={handleCameraChanged as any}
      onClick={() => onMapMove?.()}>
      {/* User location dot */}
      <AdvancedMarker position={userCenter} zIndex={100}>
        <div style={{ position: 'relative', width: 20, height: 20 }}>
          <div
            style={{
              position: 'absolute',
              inset: -6,
              borderRadius: '50%',
              backgroundColor: 'rgba(66,133,244,0.18)',
              animation: 'pulse 900ms ease-out infinite',
            }}
          />
          <div
            style={{
              position: 'absolute',
              inset: -2,
              borderRadius: '50%',
              backgroundColor: '#fff',
              boxShadow: '0 2px 8px rgba(0,0,0,0.35)',
            }}
          />
          <div
            style={{
              position: 'absolute',
              inset: 0,
              borderRadius: '50%',
              backgroundColor: '#4285F4',
            }}
          />
        </div>
      </AdvancedMarker>

      {events.map((event) => (
        isValidMapCoordinate(event.coordinate) ? (
          <EventMarker
            key={event.id}
            event={event}
            selected={event.id === selectedId}
            clusterer={clusterer}
            markersRef={markersRef}
            onSelect={onSelect}
          />
        ) : null
      ))}
    </Map>
  );
}

const EventMarker = React.memo(function EventMarker({
  event,
  selected,
  clusterer,
  markersRef,
  onSelect,
}: {
  event: RadiousEvent;
  selected: boolean;
  clusterer: MarkerClusterer | null;
  markersRef: React.RefObject<globalThis.Map<string, GoogleAdvancedMarker>>;
  onSelect: (id: string) => void;
}) {
  const [base] = getEventPalette(event.id);
  const onSelectRef = React.useRef(onSelect);
  const boostTier = event.is_boosted ? event.boost_tier : undefined;
  const isIgniteBoost = boostTier === BoostTierIgnite;
  const isBlazeBoost = boostTier === BoostTierBlaze;
  const hasMapBoost = isIgniteBoost || isBlazeBoost;
  const boostBorder = isBlazeBoost ? '#ff6a3d' : isIgniteBoost ? '#f59e0b' : ACCENT;
  const pinSize = isBlazeBoost ? 58 : 52;
  const pinRadius = isBlazeBoost ? 14 : 12;
  const defaultZIndex = isBlazeBoost ? 14 : isIgniteBoost ? 9 : 1;
  const selectedZIndex = isBlazeBoost ? 24 : isIgniteBoost ? 20 : 10;
  const coverPoster =
    event.poster_images?.reduce<EventPosterImage | null>((currentCover, image) => {
      if (!image.public_url.trim()) {
        return currentCover;
      }
      if (!currentCover || image.position < currentCover.position) {
        return image;
      }
      return currentCover;
    }, null) ?? null;
  const posterURL = coverPoster?.public_url ?? event.poster_url;
  const focalX = coverPoster?.focal_point_x ?? 0.5;
  const focalY = coverPoster?.focal_point_y ?? 0.3;
  const focalScale = coverPoster?.focal_scale ?? 1;
  const visualKey = [
    selected ? 'selected' : 'idle',
    base,
    boostBorder,
    String(hasMapBoost),
    String(isBlazeBoost),
    String(pinRadius),
    String(pinSize),
    posterURL,
    String(focalX),
    String(focalY),
    String(focalScale),
    event.title,
    event.starts_at,
    event.ends_at,
    event.venue_name,
  ].join('|');
  React.useEffect(() => {
    onSelectRef.current = onSelect;
  }, [onSelect]);

  // Build a DOM element for the flyer pin
  const content = React.useMemo(() => {
    const wrap = document.createElement('div');
    wrap.style.cssText = `
      position: relative;
      display: flex; flex-direction: column; align-items: center;
      cursor: pointer;
      transform: ${selected ? 'scale(1.18)' : 'scale(1)'};
      transition: transform 0.15s ease;
      overflow: visible;
    `;

    if (hasMapBoost) {
      const ring = document.createElement('div');
      ring.style.cssText = `
        position: absolute;
        top: ${isBlazeBoost ? '-10px' : '-7px'};
        width: ${isBlazeBoost ? '78px' : '66px'};
        height: ${isBlazeBoost ? '78px' : '66px'};
        border-radius: ${isBlazeBoost ? '22px' : '18px'};
        border: ${isBlazeBoost ? '3px' : '2px'} solid ${
          isBlazeBoost ? 'rgba(255,106,61,0.78)' : 'rgba(245,158,11,0.62)'
        };
        background: ${isBlazeBoost ? 'rgba(255,106,61,0.12)' : 'rgba(245,158,11,0.08)'};
        box-shadow: 0 10px 30px ${isBlazeBoost ? 'rgba(255,106,61,0.28)' : 'rgba(245,158,11,0.22)'};
      `;
      wrap.appendChild(ring);
    }

    const pin = document.createElement('div');
    pin.style.cssText = `
      width: ${pinSize}px; height: ${pinSize}px; border-radius: ${pinRadius}px; overflow: hidden;
      border: ${isBlazeBoost ? '3px' : '2px'} solid ${boostBorder}; background: ${base};
      box-shadow: ${
        selected
          ? `0 0 0 3px rgba(239,78,46,0.35), 0 6px 18px rgba(0,0,0,0.5)`
          : hasMapBoost
            ? `0 10px 26px ${isBlazeBoost ? 'rgba(255,106,61,0.32)' : 'rgba(245,158,11,0.25)'}, 0 4px 12px rgba(0,0,0,0.45)`
            : '0 4px 12px rgba(0,0,0,0.45)'
      };
    `;

    if (posterURL) {
      const img = document.createElement('img');
      img.src = posterURL;
      img.draggable = false;
      img.style.cssText = `
        width:100%;height:100%;object-fit:cover;display:block;
        object-position:${focalX * 100}% ${focalY * 100}%;
        transform:scale(${Math.max(1, focalScale)});
        transform-origin:${focalX * 100}% ${focalY * 100}%;
      `;
      img.alt = '';
      pin.appendChild(img);
    }

    const tail = document.createElement('div');
    tail.style.cssText = `
      margin-top: -1px; width: 9px; height: 9px;
      transform: rotate(45deg);
      border-right: 2px solid ${boostBorder}; border-bottom: 2px solid ${boostBorder};
      background: #161616;
    `;

    wrap.appendChild(pin);
    wrap.appendChild(tail);

    const preview = document.createElement('div');
    preview.style.cssText = `
      position: absolute;
      left: 50%;
      bottom: calc(100% + 8px);
      width: 220px;
      transform: translateX(-50%) translateY(4px);
      border: 1px solid rgba(239,78,46,0.38);
      border-radius: 14px;
      background: rgba(15,13,10,0.94);
      box-shadow: 0 14px 32px rgba(0,0,0,0.36);
      color: #fffaf0;
      opacity: 0;
      pointer-events: none;
      padding: 10px 11px;
      transition: opacity 120ms ease, transform 120ms ease;
      z-index: 1000;
    `;
    preview.innerHTML = `
      <div style="
        overflow:hidden;
        text-overflow:ellipsis;
        white-space:nowrap;
        font-size:13px;
        font-weight:800;
        line-height:17px;
      ">${escapeHTML(event.title)}</div>
      <div style="
        margin-top:3px;
        overflow:hidden;
        text-overflow:ellipsis;
        white-space:nowrap;
        color:rgba(255,250,240,0.72);
        font-size:11px;
        font-weight:700;
        line-height:15px;
      ">${escapeHTML(formatEventTimeRange(event.starts_at, event.ends_at))}</div>
      <div style="
        margin-top:2px;
        overflow:hidden;
        text-overflow:ellipsis;
        white-space:nowrap;
        color:rgba(255,250,240,0.58);
        font-size:11px;
        font-weight:700;
        line-height:15px;
      ">${escapeHTML(event.venue_name)}</div>
    `;
    wrap.appendChild(preview);
    wrap.addEventListener('mouseenter', () => {
      preview.style.opacity = '1';
      preview.style.transform = 'translateX(-50%) translateY(0)';
    });
    wrap.addEventListener('mouseleave', () => {
      preview.style.opacity = '0';
      preview.style.transform = 'translateX(-50%) translateY(4px)';
    });
    return wrap;
  }, [
    base,
    boostBorder,
    event.ends_at,
    focalScale,
    focalX,
    focalY,
    hasMapBoost,
    isBlazeBoost,
    pinRadius,
    pinSize,
    posterURL,
    selected,
    event.starts_at,
    event.title,
    event.venue_name,
  ]);
  const markerStateRef = React.useRef({
    content,
    defaultZIndex,
    latitude: event.coordinate.latitude,
    longitude: event.coordinate.longitude,
    selected,
    selectedZIndex,
    visualKey,
  });

  React.useEffect(() => {
    markerStateRef.current = {
      content,
      defaultZIndex,
      latitude: event.coordinate.latitude,
      longitude: event.coordinate.longitude,
      selected,
      selectedZIndex,
      visualKey,
    };
  }, [
    content,
    defaultZIndex,
    event.coordinate.latitude,
    event.coordinate.longitude,
    selected,
    selectedZIndex,
    visualKey,
  ]);

  React.useEffect(() => {
    if (!clusterer) return;
    const markerMap = markersRef.current;
    const markerState = markerStateRef.current;
    const marker = new google.maps.marker.AdvancedMarkerElement({
      position: { lat: markerState.latitude, lng: markerState.longitude },
      content: markerState.content,
      zIndex: markerState.selected ? markerState.selectedZIndex : markerState.defaultZIndex,
    });
    marker.__radiousVisualKey = markerState.visualKey;
    const removeClickListener = registerMarkerClick(marker, (clickEvent) => {
      clickEvent?.stop?.();
      clickEvent?.domEvent?.stopPropagation?.();
      onSelectRef.current(event.id);
    });
    markerMap?.set(event.id, marker);
    clusterer.addMarker(marker);

    return () => {
      removeClickListener();
      clusterer.removeMarker(marker);
      markerMap?.delete(event.id);
    };
  }, [clusterer, event.id, markersRef]);

  React.useEffect(() => {
    const marker = markersRef.current.get(event.id);
    if (!marker) {
      return;
    }

    marker.position = { lat: event.coordinate.latitude, lng: event.coordinate.longitude };
    marker.zIndex = selected ? selectedZIndex : defaultZIndex;
    if (marker.__radiousVisualKey !== visualKey) {
      marker.content = content;
      marker.__radiousVisualKey = visualKey;
    }
  }, [
    content,
    defaultZIndex,
    event.coordinate.latitude,
    event.coordinate.longitude,
    event.id,
    markersRef,
    selected,
    selectedZIndex,
    visualKey,
  ]);

  return null;
});
